import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import paymentsService from '../services/paymentsService';
import Loader from '../components/Loader';
import styles from '../assets/markAsPaidPayment.module.css';

const MarkAsPaidPayment = () => {
  const { rentId, paymentId } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [formData, setFormData] = useState({
    amount: '',
    amount_paid: '',
    due_date: '',
    payment_date: '',
    bank_account_id: '2', // Default to ID 2 as per Laravel
    status: '1' // Default to 'Paid' (1)
  });
  const [bankAccounts, setBankAccounts] = useState([]);
  const [payment, setPayment] = useState(null);
  const [guardian, setGuardian] = useState(null);
  const [rent, setRent] = useState(null);
  const [property, setProperty] = useState(null);
  const [room, setRoom] = useState(null);

  // Refs for date picker inputs
  const dueDateInputRef = useRef(null);
  const paymentDateInputRef = useRef(null);

  // Validation state for date fields
  const [dateErrors, setDateErrors] = useState({
    due_date: '',
    payment_date: ''
  });

  useEffect(() => {
    loadPaymentData();
  }, [rentId, paymentId]);

  const loadPaymentData = async () => {
    try {
      setLoading(true);
      
      // TEMPORARY FIX: Use direct fetch instead of axios to bypass 422 error
      // The backend is working correctly (direct fetch returns 200 OK)
      // This is likely an axios configuration or caching issue
      const token = localStorage.getItem('token');
      const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:3001';
      const baseUrl = apiUrl.replace(/\/+$/, '') + (apiUrl.includes('/api') ? '' : '/api');
      
      const fetchResponse = await fetch(
        `${baseUrl}/payments/rent/${rentId}/${paymentId}/mark-as-paid`,
        {
          method: 'GET',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );
      
      if (!fetchResponse.ok) {
        const errorData = await fetchResponse.json();
        throw new Error(errorData.message || `HTTP ${fetchResponse.status}`);
      }
      
      const response = await fetchResponse.json();
      console.log('📡 [MARK_AS_PAID] Direct fetch response:', response);

      if (response && response.success && response.data) {
        const data = response.data;
        setPayment(data.payment);
        setGuardian(data.guardian);
        setRent(data.rent);
        setProperty(data.property);
        setRoom(data.room);
        setBankAccounts(data.bankAccounts || []);

        // Use toPay from backend response (already calculated) or calculate it
        // Backend returns: toPay: payment.amount - (payment.amount_paid || 0)
        const toPay = data.toPay !== undefined 
          ? parseFloat(data.toPay) 
          : (parseFloat(data.payment.amount || 0) - parseFloat(data.payment.amount_paid || 0));

        console.log('💰 [MARK_AS_PAID] Payment data:', {
          amount: data.payment.amount,
          amount_paid: data.payment.amount_paid,
          toPay_from_backend: data.toPay,
          toPay_calculated: toPay
        });

        // Set form data with Laravel defaults
        setFormData({
          amount: data.payment.amount || '',
          amount_paid: toPay.toString(), // Auto-fill with remaining amount (convert to string for input)
          due_date: data.payment.due_date ? formatDateForInput(data.payment.due_date) : '',
          payment_date: getCurrentDate(), // Default to today's date (Laravel: $now->format('d/m/Y'))
          bank_account_id: data.payment.bank_account_id || '2',
          status: '1' // Default to 'Paid' when marking as paid
        });
      } else {
        console.error('❌ [MARK_AS_PAID] Invalid response structure:', response);
        toast.error("Failed to load payment data: Invalid response structure");
      }
    } catch (error) {
      console.error('❌ [MARK_AS_PAID] Error loading payment data:', error);
      console.error('❌ [MARK_AS_PAID] Error details:', {
        message: error.message,
        response: error.response?.data,
        status: error.response?.status
      });
      toast.error("Failed to load payment data");
    } finally {
      setLoading(false);
    }
  };

  // Format date from ISO to dd/mm/yyyy for display
  const formatDateForInput = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = date.getFullYear();
    return `${day}/${month}/${year}`;
  };

  // Get current date in dd/mm/yyyy format (matching Laravel $now->format('d/m/Y'))
  const getCurrentDate = () => {
    const now = new Date();
    const day = String(now.getDate()).padStart(2, '0');
    const month = String(now.getMonth() + 1).padStart(2, '0');
    const year = now.getFullYear();
    return `${day}/${month}/${year}`;
  };

  // Convert dd/mm/yyyy to yyyy-mm-dd for backend
  const formatDateForBackend = (dateString) => {
    if (!dateString) return null;
    const parts = dateString.split('/');
    if (parts.length !== 3) return null;
    return `${parts[2]}-${parts[1]}-${parts[0]}`; // yyyy-mm-dd
  };

  // Convert DD/MM/YYYY to YYYY-MM-DD for native date input
  const convertToDateInputFormat = (dateString) => {
    if (!dateString) return '';
    // If already in YYYY-MM-DD format, return as is
    if (dateString.includes('-') && /^\d{4}-\d{2}-\d{2}$/.test(dateString)) {
      return dateString;
    }
    // Handle DD/MM/YYYY format (Laravel format)
    if (dateString.includes('/')) {
      const parts = dateString.split('/');
      if (parts.length === 3) {
        const [day, month, year] = parts;
        return `${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`;
      }
    }
    return '';
  };

  // Convert YYYY-MM-DD to DD/MM/YYYY (Laravel format)
  const convertFromDateInputFormat = (dateString) => {
    if (!dateString) return '';
    if (dateString.includes('-')) {
      const [year, month, day] = dateString.split('-');
      return `${day}/${month}/${year}`;
    }
    return dateString;
  };

  // Validate date format dd/mm/yyyy
  const validateDateFormat = (dateString) => {
    if (!dateString) return { valid: true, error: '' }; // Empty is allowed

    // Check format pattern
    const dateRegex = /^(\d{1,2})\/(\d{1,2})\/(\d{4})$/;
    const match = dateString.match(dateRegex);

    if (!match) {
      return { valid: false, error: 'Invalid format. Use dd/mm/yyyy' };
    }

    const day = parseInt(match[1], 10);
    const month = parseInt(match[2], 10);
    const year = parseInt(match[3], 10);

    // Validate ranges
    if (month < 1 || month > 12) {
      return { valid: false, error: 'Invalid month (1-12)' };
    }

    if (day < 1 || day > 31) {
      return { valid: false, error: 'Invalid day (1-31)' };
    }

    // Check days in month
    const daysInMonth = new Date(year, month, 0).getDate();
    if (day > daysInMonth) {
      return { valid: false, error: `Invalid day for this month (max ${daysInMonth})` };
    }

    // Validate year range (reasonable range)
    if (year < 2000 || year > 2100) {
      return { valid: false, error: 'Invalid year (2000-2100)' };
    }

    return { valid: true, error: '' };
  };

  // Handle manual date input with validation
  const handleDateInputChange = (fieldName, e) => {
    const value = e.target.value;

    // Update the form data
    setFormData(prev => ({
      ...prev,
      [fieldName]: value
    }));

    // Validate the date format
    const validation = validateDateFormat(value);
    setDateErrors(prev => ({
      ...prev,
      [fieldName]: validation.error
    }));
  };

  // Handle blur - format the date if valid
  const handleDateBlur = (fieldName) => {
    const value = formData[fieldName];
    if (!value) return;

    const validation = validateDateFormat(value);
    if (validation.valid && value) {
      // Ensure proper formatting (pad day/month with zeros)
      const parts = value.split('/');
      if (parts.length === 3) {
        const day = parts[0].padStart(2, '0');
        const month = parts[1].padStart(2, '0');
        const year = parts[2];
        const formattedDate = `${day}/${month}/${year}`;
        setFormData(prev => ({
          ...prev,
          [fieldName]: formattedDate
        }));
      }
    }
  };

  // Handle date change from native date picker
  const handleDateChange = (fieldName, e) => {
    const dateValue = e.target.value;
    if (dateValue) {
      const formattedDate = convertFromDateInputFormat(dateValue);
      setFormData(prev => ({
        ...prev,
        [fieldName]: formattedDate
      }));
      // Clear any error when selecting from calendar
      setDateErrors(prev => ({
        ...prev,
        [fieldName]: ''
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        [fieldName]: ''
      }));
    }
  };


  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validate date formats before submission
    const dueDateValidation = validateDateFormat(formData.due_date);
    const paymentDateValidation = validateDateFormat(formData.payment_date);

    if (!dueDateValidation.valid || !paymentDateValidation.valid) {
      setDateErrors({
        due_date: dueDateValidation.error,
        payment_date: paymentDateValidation.error
      });
      toast.error("Please correct the date format errors");
      return;
    }

    // Validation - Amount can be empty/0 since field is disabled and comes from backend
    const amount = parseFloat(formData.amount) || 0;
    if (amount < 0) {
      toast.error("Amount must be 0 or greater");
      return;
    }
    const amountPaid = parseFloat(formData.amount_paid) || 0;
    if (amountPaid < 0) {
      toast.error("Amount paid must be 0 or greater");
      return;
    }
    if (!formData.bank_account_id) {
      toast.error("Bank account is required");
      return;
    }

    try {
      // Prepare data for backend (matching Laravel structure)
      // Use validated amount/amountPaid values which default to 0 for empty strings
      const updateData = {
        amount: amount,
        amount_paid: amountPaid,
        due_date: formatDateForBackend(formData.due_date),
        payment_date: formatDateForBackend(formData.payment_date),
        bank_account_id: parseInt(formData.bank_account_id),
        status: parseInt(formData.status)
      };

      // Use mark-as-paid endpoint instead of edit endpoint
      const response = await fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:3001'}/api/payments/rent/${rentId}/${paymentId}/mark-as-paid`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${localStorage.getItem("token")}`
        },
        body: JSON.stringify(updateData)
      });

      const data = await response.json();

      if (!response.ok) {
        // Handle validation errors
        if (response.status === 422 && data.errors) {
          const errorMessages = Object.values(data.errors).flat().join(', ');
          toast.error(`Validation failed: ${errorMessages}`);
        } else {
          toast.error(data.message || "Failed to mark payment as paid");
        }
        return;
      }

      if (data.success) {
        toast.success("Payment has been marked as paid successfully!");
        // Navigate back to rent payments or guardian payments
        navigate(`/rents/${rentId}/payments`);
      } else {
        toast.error(data.message || "Failed to mark payment as paid");
      }
    } catch (error) {
      console.error('Error marking payment as paid:', error);
      toast.error("Failed to mark payment as paid");
    }
  };

  const handleCancel = () => {
    navigate(`/rents/${rentId}/payments`);
  };

  if (loading) return <Loader />;

  return (
    <div className={styles.markAsPaidContainer}>
      {/* Breadcrumbs */}
      <div className={styles.breadcrumb}>
        <a href="/dashboard">Dashboard</a>
        <span> / </span>
        <span>Finance</span>
        <span> / </span>
        <a href="/payments">Payments</a>
        <span> / </span>
        <span>Mark as Paid</span>
      </div>

      {/* Guardian Overview (matching Laravel) */}
      {guardian && (
        <div className={styles.guardianOverview}>
          <div className={styles.guardianHeader}>
            <h3>Guardian: {guardian.first_name} {guardian.last_name}</h3>
            <p>Mobile: {guardian.mobile_number || guardian.mobile || '-'}</p>
            <p>Email: {guardian.email || '-'}</p>
          </div>
        </div>
      )}

      {/* Property and Room Details (matching Laravel) */}
      {property && room && (
        <div className={styles.propertyDetails}>
          <div className={styles.detailsCard}>
            <h4>Property Details</h4>
            <p><strong>Property:</strong> {property.name}</p>
            <p><strong>Room:</strong> {room.name}</p>
            <p><strong>Postcode:</strong> {property.postcode}</p>
          </div>
        </div>
      )}

      {/* Mark as Paid Form */}
      <div className={styles.formContainer}>
        <div className={styles.panelTitle}>
          <h3>Mark Payment as Paid</h3>
        </div>

        <form onSubmit={handleSubmit} className={styles.markAsPaidForm}>
          <div className={styles.formGroup}>
            <label className={styles.labelRequired}>Amount</label>
            <input
              type="number"
              step="0.01"
              name="amount"
              value={formData.amount}
              onChange={handleInputChange}
              className={styles.formControl}
              required
            />
            <small className={styles.helpText}>Total payment amount</small>
          </div>

          <div className={styles.formGroup}>
            <label className={styles.labelRequired}>Amount Paid</label>
            <input
              type="number"
              step="0.01"
              name="amount_paid"
              value={formData.amount_paid}
              onChange={handleInputChange}
              className={styles.formControl}
              required
            />
            <small className={styles.helpText}>Amount being paid now (auto-filled with remaining balance)</small>
          </div>

          <div className={styles.formGroup}>
            <label>Due Date</label>
            <div style={{ position: 'relative', display: 'flex', alignItems: 'center' }}>
              <input
                type="text"
                name="due_date"
                value={formData.due_date}
                onChange={(e) => handleDateInputChange('due_date', e)}
                onBlur={() => handleDateBlur('due_date')}
                className={`${styles.formControl} ${dateErrors.due_date ? styles.inputError : ''}`}
                placeholder="dd/mm/yyyy"
                style={{ paddingRight: '40px' }}
              />
              {/* Calendar button with native date input overlay */}
              <div
                className={styles.calendarButton}
                style={{
                  position: 'absolute',
                  right: '1px',
                  top: '1px',
                  bottom: '1px',
                  width: '36px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  cursor: 'pointer',
                  backgroundColor: '#f8f9fa',
                  borderLeft: '1px solid #ccc',
                  borderRadius: '0 3px 3px 0'
                }}
              >
                <input
                  ref={dueDateInputRef}
                  type="date"
                  style={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    width: '100%',
                    height: '100%',
                    opacity: 0,
                    cursor: 'pointer'
                  }}
                  value={convertToDateInputFormat(formData.due_date)}
                  onChange={(e) => handleDateChange('due_date', e)}
                />
                <svg
                  width="16"
                  height="16"
                  viewBox="0 0 16 16"
                  fill="#666"
                  style={{ pointerEvents: 'none' }}
                >
                  <path d="M3.5 0a.5.5 0 0 1 .5.5V1h8V.5a.5.5 0 0 1 1 0V1h1a2 2 0 0 1 2 2v11a2 2 0 0 1-2 2H2a2 2 0 0 1-2-2V3a2 2 0 0 1 2-2h1V.5a.5.5 0 0 1 .5-.5zM1 4v10a1 1 0 0 0 1 1h12a1 1 0 0 0 1-1V4H1z"/>
                </svg>
              </div>
            </div>
            {dateErrors.due_date ? (
              <small className={styles.errorText}>{dateErrors.due_date}</small>
            ) : (
              <small className={styles.helpText}>Format: dd/mm/yyyy</small>
            )}
          </div>

          <div className={styles.formGroup}>
            <label>Payment Date</label>
            <div style={{ position: 'relative', display: 'flex', alignItems: 'center' }}>
              <input
                type="text"
                name="payment_date"
                value={formData.payment_date}
                onChange={(e) => handleDateInputChange('payment_date', e)}
                onBlur={() => handleDateBlur('payment_date')}
                className={`${styles.formControl} ${dateErrors.payment_date ? styles.inputError : ''}`}
                placeholder="dd/mm/yyyy"
                style={{ paddingRight: '40px' }}
              />
              {/* Calendar button with native date input overlay */}
              <div
                className={styles.calendarButton}
                style={{
                  position: 'absolute',
                  right: '1px',
                  top: '1px',
                  bottom: '1px',
                  width: '36px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  cursor: 'pointer',
                  backgroundColor: '#f8f9fa',
                  borderLeft: '1px solid #ccc',
                  borderRadius: '0 3px 3px 0'
                }}
              >
                <input
                  ref={paymentDateInputRef}
                  type="date"
                  style={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    width: '100%',
                    height: '100%',
                    opacity: 0,
                    cursor: 'pointer'
                  }}
                  value={convertToDateInputFormat(formData.payment_date)}
                  onChange={(e) => handleDateChange('payment_date', e)}
                />
                <svg
                  width="16"
                  height="16"
                  viewBox="0 0 16 16"
                  fill="#666"
                  style={{ pointerEvents: 'none' }}
                >
                  <path d="M3.5 0a.5.5 0 0 1 .5.5V1h8V.5a.5.5 0 0 1 1 0V1h1a2 2 0 0 1 2 2v11a2 2 0 0 1-2 2H2a2 2 0 0 1-2-2V3a2 2 0 0 1 2-2h1V.5a.5.5 0 0 1 .5-.5zM1 4v10a1 1 0 0 0 1 1h12a1 1 0 0 0 1-1V4H1z"/>
                </svg>
              </div>
            </div>
            {dateErrors.payment_date ? (
              <small className={styles.errorText}>{dateErrors.payment_date}</small>
            ) : (
              <small className={styles.helpText}>Format: dd/mm/yyyy (defaults to today)</small>
            )}
          </div>

          <div className={styles.formGroup}>
            <label className={styles.labelRequired}>GG Bank Account</label>
            <select
              name="bank_account_id"
              value={formData.bank_account_id}
              onChange={handleInputChange}
              className={styles.formControl}
              required
            >
              <option value="">Select Bank Account</option>
              {bankAccounts.map(account => (
                <option key={account.id} value={account.id}>
                  {account.name}
                </option>
              ))}
            </select>
          </div>

          <div className={styles.formGroup}>
            <label className={styles.labelRequired}>Status</label>
            <select
              name="status"
              value={formData.status}
              onChange={handleInputChange}
              className={styles.formControl}
              required
            >
              <option value="0">Not paid</option>
              <option value="1">Paid</option>
              <option value="4">Plan</option>
            </select>
          </div>

          <div className={styles.formActions}>
            <button
              type="button"
              onClick={handleCancel}
              className={styles.btnCancel}
            >
              Cancel
            </button>
            <button
              type="submit"
              className={styles.btnSubmit}
            >
              <i className="glyphicon glyphicon-ok"></i> Save
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default MarkAsPaidPayment;
